import TaflBoard from './board';
import TaflRules from './rules';
import Fort from './fort';

// TODO
// surrounds
// exit-forts
// draws

import Game from '../../game';

class Tafl extends Game {
  ['constructor']!: typeof Tafl

  n: number;

  lastCaptured: string[]|null;

  lastPlayed: string[];

  askedFor: string;

  playableSquares: string[];

  chars: string;

  board: TaflBoard;

  rules: TaflRules;

  constructor(obj) {
    super(obj);
    if (obj.the_game) {
      obj = this.constructor.deserialize(obj.the_game);
    }
    // game related
    this.sides = obj.sides || ['d', 'a'];
    this.sideNames = obj.sideNames || { d: 'Defenders', a: 'Attackers' };
    this.lastCaptured = obj.lastCaptured || [];
    this.lastPlayed = obj.lastPlayed || [];
    this.askedFor = obj.askedFor || '';
    this.playableSquares = [];
    this.board = obj.board || new TaflBoard(obj.config.board);
    this.rules = obj.rules || new TaflRules(obj.config.rules);
    this.n = this.board.length;
    this.chars = 'abcdefghijklmnopqrstuvxyz';
    this.playSound(this.lastCaptured.length);
  }

  static getNumValues(str) {
    const [row, col] = str.split('_');
    return [Number.parseInt(row, 10), Number.parseInt(col, 10)];
  }

  toNotation(str) {
    const [row, col] = this.constructor.getNumValues(str);
    return `${this.chars[col]}${this.n - row}`;
  }

  getNotation({ lastPlayed, lastCaptured = null, gameOver }): string {
    const [from, to] = lastPlayed.map(lp => this.toNotation(lp));
    let str = `${from}-${to}`;
    if (lastCaptured && lastCaptured.length > 0) {
      const cn = lastCaptured.map(cp => this.toNotation(cp));
      str += `x${cn.join(' /')}`;
    }
    if (gameOver) {
      str += '++';
    }

    return str;
  }

  getPlayableSquares(row, col) {
    let rowCount = row - 1;
    const squares = [];
    const kind = this.board[row][col];

    while (rowCount >= 0 && this.isEmpty(rowCount, col)) {
      squares.push(`${rowCount}_${col}`);
      rowCount -= 1;
    }
    rowCount = row + 1;
    while (rowCount <= this.n - 1 && this.isEmpty(rowCount, col)) {
      squares.push(`${rowCount}_${col}`);
      rowCount += 1;
    }

    let colCount = col - 1;
    while (colCount >= 0 && this.isEmpty(row, colCount)) {
      squares.push(`${row}_${colCount}`);
      colCount -= 1;
    }

    colCount = col + 1;
    while (colCount <= this.n - 1 && this.isEmpty(row, colCount)) {
      squares.push(`${row}_${colCount}`);
      colCount += 1;
    }

    for (let i = squares.length - 1; i >= 0; i -= 1) {
      const [curRow, curCol] = this.constructor.getNumValues(squares[i]);

      if (this.isBase(curRow, curCol) && kind !== 'd k') {
        squares.splice(i, 1);
      }
    }
    return squares;
  }

  setPlayableSquares(row, col) {
    if (this.board[row][col] !== ' ') {
      const askedFor = `${row}_${col}`;
      if (this.askedFor === askedFor) { // clicking on the same square
        this.askedFor = '';
        this.playableSquares = [];
      } else { // clicking on a piece of the current player
        const askedAttackerOnAttackersTurn = this.getTurnPlayerSide() === 'a' && this.isAttacker(row, col);
        const askedDefenderOnDefendersTurn = this.getTurnPlayerSide() === 'd' && this.isDfOrKing(row, col);
        if (askedAttackerOnAttackersTurn || askedDefenderOnDefendersTurn) {
          this.askedFor = askedFor;
          this.playableSquares = this.getPlayableSquares(row, col);
        }
      }
    }
  }

  move(from, to) {
    const [row, col] = from;
    const [newr, newc] = to;

    const piece = this.board[row][col];
    this.lastPlayed = [`${row}_${col}`, `${newr}_${newc}`];
    this.board[row][col] = ' ';
    this.board[newr][newc] = piece;
    this.playableSquares = [];
    this.askedFor = '';

    if (this.isGameOver()) {
      const notation = this.getNotation({
        lastPlayed: this.lastPlayed,
        gameOver: true,
      });
      const result = this.getTurnPlayerSide() === 'a' ? 'Attackers captured the king' : 'King escaped';
      this.gameOver(this.getTurnPlayerSide(), notation, result);

      return;
    }

    this.beforeTurnChange();
    this.getTurnPlayerInfo().clockStarted = true;
    this.turn += 1;
    this.lastCaptured = this.checkCaptures();
    const notation = this.getNotation({
      lastPlayed: this.lastPlayed,
      lastCaptured: this.lastCaptured,
      gameOver: false,
    });
    this.addMove(notation);
  }

  canMakeAMove(kind): boolean {
    const side = kind.charAt(0);
    for (let r = 0; r < this.n; r += 1) {
      for (let c = 0; c < this.n; c += 1) {
        const attackerAndCanMove = side === 'a' && this.isAttacker(r, c) && this.canMoveThePiece(r, c);
        const defenderAndCanMove = side === 'd' && this.isDfOrKing(r, c) && this.canMoveThePiece(r, c);
        if (attackerAndCanMove || defenderAndCanMove) {
          return true;
        }
      }
    }

    return false;
  }

  isGameOver() {
    const to = this.constructor.getNumValues(this.lastPlayed[1]);
    const side = this.getTurnPlayerSide();
    if (side === 'a') { // attacker, king should be captured
      let [kingOnTop, kingOnRight, kingOnBottom, kingOnLeft] = [false, false, false, false];
      kingOnTop = to[0] > 1 && this.isKing(to[0] - 1, to[1]);
      kingOnBottom = to[0] < this.n - 2 && this.isKing(to[0] + 1, to[1]);
      kingOnLeft = to[1] > 1 && this.isKing(to[0], to[1] - 1);
      kingOnRight = to[1] < this.n - 2 && this.isKing(to[0], to[1] + 1);

      let kingPosition = '';
      if (kingOnTop) {
        kingPosition = `${to[0] - 1}_${to[1]}`;
      } else if (kingOnBottom) {
        kingPosition = `${to[0] + 1}_${to[1]}`;
      } else if (kingOnRight) {
        kingPosition = `${to[0]}_${to[1] + 1}`;
      } else if (kingOnLeft) {
        kingPosition = `${to[0]}_${to[1] - 1}`;
      }

      if (kingPosition !== '') {
        const [kr, kc] = this.constructor.getNumValues(kingPosition); // king row/col
        if (kr > 0 && kr < this.n - 1 && kc > 0 && kc < this.n - 1) { // king should be surroundable
          const tSurrounded = (this.isAttacker(kr - 1, kc) || this.isCenter(kr - 1, kc)) ? 1 : 0;
          const bSurrounded = (this.isAttacker(kr + 1, kc) || this.isCenter(kr + 1, kc)) ? 1 : 0;
          const lSurrounded = (this.isAttacker(kr, kc - 1) || this.isCenter(kr, kc - 1)) ? 1 : 0;
          const rSurrounded = (this.isAttacker(kr, kc + 1) || this.isCenter(kr, kc + 1)) ? 1 : 0;

          if (this.rules.attackerCountToCapture >= 3) {
            const sum = tSurrounded + bSurrounded + lSurrounded + rSurrounded;
            return sum >= this.rules.attackerCountToCapture;
          }

          if (this.rules.attackerCountToCapture === 2) {
            return (tSurrounded + bSurrounded === 2) || (lSurrounded + rSurrounded === 2);
          }
        }
      }
    } else { // defender, king should be on one of the corners or in an exit fort
      const isKingInACorner = this.isKing(to[0], to[1]) && this.isCorner(to[0], to[1]);
      if (isKingInACorner) {
        return true;
      }

      if (this.rules.exitForts) {
        const kingPos = [];

        // kingOnTopRow
        for (let c = 0; c < this.n; c += 1) {
          if (this.isKing(0, c)) { kingPos[0] = 0; kingPos[1] = c; }
        }

        // kingOnBottomRow
        for (let c = 0; c < this.n; c += 1) {
          if (this.isKing(this.n - 1, c)) { kingPos[0] = this.n - 1; kingPos[1] = c; }
        }

        // kingOnLeftCol
        for (let r = 0; r < this.n; r += 1) {
          if (this.isKing(r, 0)) { kingPos[0] = r; kingPos[1] = 0; }
        }

        // kingOnRightCol
        for (let r = 0; r < this.n; r += 1) {
          if (this.isKing(r, this.n - 1)) { kingPos[0] = r; kingPos[1] = this.n - 1; }
        }

        // if king is on an edge
        if (kingPos.length > 0) {
          const [r, c] = [kingPos[0], kingPos[1]];
          const kingCanGoUp = r - 1 >= 0 && this.isEmpty(r - 1, c);
          const kingCanGoDown = r + 1 <= this.n - 1 && this.isEmpty(r + 1, c);
          const kingCanGoLeft = c - 1 >= 0 && this.isEmpty(r, c - 1);
          const kingCanGoRight = c + 1 <= this.n - 1 && this.isEmpty(r, c + 1);
          const kingCanMove = kingCanGoUp || kingCanGoDown || kingCanGoLeft || kingCanGoRight;

          if (kingCanMove) {
            // return true if king is in a fort
            if (r === 0 || r === this.n - 1) { // top row or bottom row
              for (let cc = 0; cc < this.n; cc += 1) {
                if (this.isDefender(r, cc)) {
                  const fort = this.getFort(r, cc);
                  if (fort.contains(r, c)) {
                    return true;
                  }
                }
              }
            }

            if (c === 0 || c === this.n - 1) { // leftmost or rightmost col
              for (let rr = 0; rr < this.n; rr += 1) {
                if (this.isDefender(rr, c)) {
                  const fort = this.getFort(rr, c);
                  if (fort.contains(r, c)) {
                    return true;
                  }
                }
              }
            }
          }
        }

        return false;
      }
    }

    const opp = (side === 'a') ? 'd' : 'a';
    return !this.canMakeAMove(opp);
  }

  getFort(row, col) {
    return new Fort(this.board, row, col);
  }

  isCenter(row: number, col: number): boolean {
    const center = Math.floor(this.n / 2);
    return row === center && col === center;
  }

  isCorner(row: number, col: number): boolean {
    return (row === 0 || row === this.n - 1) && (col === 0 || col === this.n - 1);
  }

  isBase(row: number, col: number): boolean {
    return this.isCenter(row, col) || this.isCorner(row, col);
  }

  isPlayable(row: number, col: number): boolean {
    return this.playableSquares.indexOf(`${row}_${col}`) >= 0;
  }

  isEmpty(row: number, col: number): boolean {
    return this.board[row][col] === ' ';
  }

  isAttacker(row: number, col: number): boolean {
    return this.board[row][col] === 'a';
  }

  isKing(row: number, col: number): boolean {
    return this.board[row][col] === 'd k';
  }

  isDefender(row: number, col: number): boolean {
    return this.board[row][col] === 'd';
  }

  isDfOrKing(row: number, col: number): boolean {
    return this.isDefender(row, col) || this.isKing(row, col);
  }

  canMoveThePiece(row: number, col: number): boolean {
    return this.getPlayableSquares(row, col).length > 0;
  }

  check(row, col, kind) {
    let captured = false;
    const mid = this.board[row][col];
    if ((kind === 'a' && mid === 'd') || (kind.charAt(0) === 'd' && mid === 'a')) {
      captured = true;
    }

    return captured;
  }

  isAnEmptyBase(row, col) {
    return this.isBase(row, col) && this.isEmpty(row, col);
  }

  sameKindWithMe(row, col, kind) {
    if (kind === 'a' || kind === 'd') {
      return this.board[row][col].charAt(0) === kind;
    }
    if (kind === 'd k') {
      return this.board[row][col].charAt(0) === 'd';
    }

    return false;
  }

  canHelpCapture(row, col, kind) {
    if (kind === 'a' || this.rules.kingIsArmed) {
      return this.sameKindWithMe(row, col, kind) || this.isAnEmptyBase(row, col);
    }

    // when king is not armed
    // if I am king
    // I can't help
    if (kind === 'd k') {
      return false;
    }

    // when kind is defenders and the
    // square I'm checking contains the king
    // it can't help
    return this.isDefender(row, col) || this.isAnEmptyBase(row, col);
  }

  checkCaptures() {
    const [lpr, lpc] = this.constructor.getNumValues(this.lastPlayed[1]);
    let [capturedT, capturedB, capturedL, capturedR] = [false, false, false, false];

    const kind = this.board[lpr][lpc];

    if (lpr >= 2 && this.canHelpCapture(lpr - 2, lpc, kind)) {
      capturedT = this.check(lpr - 1, lpc, kind);
    }

    if (lpr <= this.n - 3 && this.canHelpCapture(lpr + 2, lpc, kind)) {
      capturedB = this.check(lpr + 1, lpc, kind);
    }

    if (lpc >= 2 && this.canHelpCapture(lpr, lpc - 2, kind)) {
      capturedL = this.check(lpr, lpc - 1, kind);
    }

    if (lpc <= this.n - 3 && this.canHelpCapture(lpr, lpc + 2, kind)) {
      capturedR = this.check(lpr, lpc + 1, kind);
    }

    const side = kind.charAt(0);
    const result = [];
    if (capturedT) {
      this.board[lpr - 1][lpc] = ' ';
      result.push(`${lpr - 1}_${lpc}`);
      this.getPlayerInfoBySide(side).captures += 1;
    }
    if (capturedB) {
      this.board[lpr + 1][lpc] = ' ';
      result.push(`${lpr + 1}_${lpc}`);
      this.getPlayerInfoBySide(side).captures += 1;
    }
    if (capturedL) {
      this.board[lpr][lpc - 1] = ' ';
      result.push(`${lpr}_${lpc - 1}`);
      this.getPlayerInfoBySide(side).captures += 1;
    }
    if (capturedR) {
      this.board[lpr][lpc + 1] = ' ';
      result.push(`${lpr}_${lpc + 1}`);
      this.getPlayerInfoBySide(side).captures += 1;
    }

    // check shieldwalls
    if (this.rules.shieldWalls) {
      const swCaptures = this.checkShieldWalls(lpr, lpc, kind);
      for (let i = 0; i < swCaptures.length; i += 1) {
        const [swRow, swCol] = this.constructor.getNumValues(swCaptures[i]);
        this.board[swRow][swCol] = ' ';
        result.push(swCaptures[i]);
        this.getPlayerInfoBySide(side).captures += 1;
      }
    }

    return result;
  }

  checkShieldWalls(row, col, kind) {
    const captured = [];
    const side = kind.charAt(0);
    const opp = (side === 'a') ? 'd' : 'a';

    // check top or bottom
    if (row === 0 || row === this.n - 1) {
      const [lCaptured, rCaptured] = [[], []];
      let theCol = col - 1;
      const rowBehind = (row === this.n - 1) ? this.n - 2 : 1;

      while (this.board[row][theCol].charAt(0) === opp
        && this.canHelpCapture(rowBehind, theCol, kind)) {
        if (this.board[row][theCol] === opp) { // capture if not the king
          lCaptured.push(`${row}_${theCol}`);
        }
        theCol -= 1;
      }

      if (this.canHelpCapture(row, theCol, kind)) {
        captured.push(...lCaptured);
      }

      theCol = col + 1;
      while (this.board[row][theCol].charAt(0) === opp
        && this.canHelpCapture(rowBehind, theCol, kind)) {
        if (this.board[row][theCol] === opp) { // capture if not the king
          rCaptured.push(`${row}_${theCol}`);
        }
        theCol += 1;
      }

      if (this.canHelpCapture(row, theCol, kind)) {
        captured.push(...rCaptured);
      }
    }

    // check left or right
    if (col === 0 || col === this.n - 1) {
      const [bCaptured, tCaptured] = [[], []];
      let theRow = row - 1;
      const colBehind = (col === this.n - 1) ? this.n - 2 : 1;

      while (this.board[theRow][col].charAt(0) === opp
        && this.canHelpCapture(theRow, colBehind, kind)) {
        if (this.board[theRow][col] === opp) { // capture if not the king
          bCaptured.push(`${theRow}_${col}`);
        }
        theRow -= 1;
      }

      if (this.canHelpCapture(theRow, col, kind)) {
        captured.push(...bCaptured);
      }

      theRow = row + 1;
      while (this.board[theRow][col].charAt(0) === opp
        && this.canHelpCapture(theRow, colBehind, kind)) {
        if (this.board[theRow][col] === opp) { // capture if not the king
          tCaptured.push(`${theRow}_${col}`);
        }
        theRow += 1;
      }

      if (this.canHelpCapture(theRow, col, kind)) {
        captured.push(...tCaptured);
      }
    }

    return captured;
  }
}

export default Tafl;
